/**
 * MultiLLMComparisonDashboard - Multi-LLM Ensemble Consensus Visualization
 *
 * Sprint 3 - Evidence Attribution: Build AI Trust
 *
 * Shows how multiple LLM models agreed/disagreed on template extraction:
 * - Grid layout showing each LLM's extraction results
 * - Consensus visualization (3/3 agree = high confidence)
 * - Disagreement highlighting (different values extracted)
 * - Confidence gauge showing agreement strength
 * - Field-by-field comparison table
 *
 * FERP Compliance:
 * - ✅ Data-Driven: All LLM results from backend ensemble processing
 * - ✅ Repository Pattern: Uses GraphQL client (LLM comparison data)
 * - ✅ Mantine UI: All components from Mantine (no MUI)
 * - ✅ Backend ID Generation: Uses template IDs from backend
 * - ✅ Life-Critical: Multi-LLM validation for safety-critical templates
 * - ✅ 16 Primitives: Shows consensus on primitive mappings
 */

import React, { useMemo } from 'react';
import {
  Badge,
  Box,
  Card,
  Grid,
  Group,
  Paper,
  Progress,
  RingProgress,
  ScrollArea,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconCheck,
  IconRobot,
  IconScale,
  IconShieldCheck,
} from '@tabler/icons-react';

// ============================================================
// TypeScript Interfaces
// ============================================================

export interface LLMResult {
  llmName: string; // "Claude 3.5 Sonnet", "GPT-4", "Gemini Pro"
  llmVersion?: string;
  confidence: number;
  extractedFields: Record<string, any>;
  processingTime: number; // milliseconds
  tokensUsed?: number;
}

export interface FieldComparison {
  fieldId: string;
  fieldLabel: string;
  llmValues: Record<string, any>; // LLM name -> extracted value
  consensus: boolean; // Do all LLMs agree?
  consensusValue?: any; // Value if consensus reached
  confidences: Record<string, number>; // LLM name -> confidence
}

export interface LLMEnsembleResult {
  llmResults: LLMResult[];
  consensusConfidence: number; // Overall consensus score (0-1)
  disagreementFields: string[]; // Field IDs where LLMs disagreed
  totalFields: number;
  consensusFields: number;
}

export interface MultiLLMComparisonDashboardProps {
  templateId?: string;
  ensembleResult: LLMEnsembleResult;
  onLLMClick?: (llmName: string) => void;
}

// ============================================================
// Helper Functions
// ============================================================

const getConfidenceColor = (confidence: number): string => {
  if (confidence >= 0.9) return 'green';
  if (confidence >= 0.75) return 'blue';
  if (confidence >= 0.6) return 'yellow';
  return 'red';
};

const getLLMIcon = (llmName: string): React.ReactNode => {
  // Different robot icons could be used per LLM, but for now use same
  return <IconRobot size={16} />;
};

const formatDuration = (ms: number): string => {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60000).toFixed(1)}m`;
};

// ============================================================
// Component
// ============================================================

export const MultiLLMComparisonDashboard: React.FC<MultiLLMComparisonDashboardProps> = ({
  templateId,
  ensembleResult,
  onLLMClick,
}) => {
  // Calculate consensus percentage
  const consensusPercentage = useMemo(() => {
    if (ensembleResult.totalFields === 0) return 0;
    return (ensembleResult.consensusFields / ensembleResult.totalFields) * 100;
  }, [ensembleResult.consensusFields, ensembleResult.totalFields]);

  // Build field-by-field comparison table
  const fieldComparisons = useMemo((): FieldComparison[] => {
    const fieldMap = new Map<string, FieldComparison>();

    // Collect all unique field IDs from all LLMs
    ensembleResult.llmResults.forEach((llmResult) => {
      Object.keys(llmResult.extractedFields).forEach((fieldId) => {
        if (!fieldMap.has(fieldId)) {
          fieldMap.set(fieldId, {
            fieldId,
            fieldLabel: fieldId, // In real implementation, would map to actual label
            llmValues: {},
            consensus: true,
            confidences: {},
          });
        }
        const comparison = fieldMap.get(fieldId)!;
        comparison.llmValues[llmResult.llmName] = llmResult.extractedFields[fieldId];
        comparison.confidences[llmResult.llmName] = llmResult.confidence;
      });
    });

    // Check consensus for each field
    fieldMap.forEach((comparison) => {
      const values = Object.values(comparison.llmValues);
      if (values.length < 2) {
        comparison.consensus = true; // Only one LLM extracted this field
        comparison.consensusValue = values[0];
      } else {
        // Check if all values are equal (stringified for deep comparison)
        const firstValue = JSON.stringify(values[0]);
        const allEqual = values.every((v) => JSON.stringify(v) === firstValue);
        comparison.consensus = allEqual;
        if (allEqual) {
          comparison.consensusValue = values[0];
        }
      }
    });

    return Array.from(fieldMap.values());
  }, [ensembleResult.llmResults]);

  // Get consensus status color
  const getConsensusStatusColor = (): string => {
    if (consensusPercentage >= 90) return 'green';
    if (consensusPercentage >= 75) return 'blue';
    if (consensusPercentage >= 60) return 'yellow';
    return 'red';
  };

  // Get consensus status label
  const getConsensusStatusLabel = (): string => {
    if (consensusPercentage >= 90) return 'High Consensus';
    if (consensusPercentage >= 75) return 'Good Consensus';
    if (consensusPercentage >= 60) return 'Moderate Consensus';
    return 'Low Consensus';
  };

  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder>
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between">
          <Title order={3}>Multi-LLM Comparison</Title>
          <Badge
            color={getConsensusStatusColor()}
            leftSection={<IconScale size={14} />}
            size="lg"
            variant="filled"
          >
            {getConsensusStatusLabel()}
          </Badge>
        </Group>

        {/* LLM Confidence Cards */}
        <SimpleGrid cols={ensembleResult.llmResults.length} spacing="md">
          {ensembleResult.llmResults.map((llmResult) => (
            <Card
              key={llmResult.llmName}
              withBorder
              padding="md"
              style={{ cursor: onLLMClick ? 'pointer' : 'default' }}
              onClick={() => onLLMClick?.(llmResult.llmName)}
            >
              <Stack gap="sm">
                <Group justify="space-between">
                  <Group gap="xs">
                    {getLLMIcon(llmResult.llmName)}
                    <Text size="sm" fw={600}>
                      {llmResult.llmName}
                    </Text>
                  </Group>
                  <Badge size="xs" color={getConfidenceColor(llmResult.confidence)}>
                    {(llmResult.confidence * 100).toFixed(0)}%
                  </Badge>
                </Group>

                {llmResult.llmVersion && (
                  <Text size="xs" c="dimmed">
                    Version: {llmResult.llmVersion}
                  </Text>
                )}

                <Box>
                  <Text size="xs" c="dimmed" mb={4}>
                    Fields Extracted
                  </Text>
                  <Text size="lg" fw={600}>
                    {Object.keys(llmResult.extractedFields).length}
                  </Text>
                </Box>

                <Box>
                  <Text size="xs" c="dimmed" mb={4}>
                    Processing Time
                  </Text>
                  <Text size="sm">{formatDuration(llmResult.processingTime)}</Text>
                </Box>

                {llmResult.tokensUsed && (
                  <Box>
                    <Text size="xs" c="dimmed" mb={4}>
                      Tokens Used
                    </Text>
                    <Text size="sm">{llmResult.tokensUsed.toLocaleString()}</Text>
                  </Box>
                )}
              </Stack>
            </Card>
          ))}
        </SimpleGrid>

        {/* Consensus Gauge */}
        <Paper withBorder p="md">
          <Group justify="space-between" ta="center">
            <Stack gap="xs">
              <Text size="sm" fw={600}>
                Consensus Confidence
              </Text>
              <Text size="xs" c="dimmed">
                {ensembleResult.consensusFields} of {ensembleResult.totalFields} fields in
                agreement
              </Text>
            </Stack>

            <RingProgress
              size={120}
              thickness={12}
              sections={[
                {
                  value: consensusPercentage,
                  color: getConsensusStatusColor(),
                },
              ]}
              label={
                <Text c={getConsensusStatusColor()} fw={700} ta="center" size="lg">
                  {consensusPercentage.toFixed(0)}%
                </Text>
              }
            />

            <Stack gap="xs">
              <Group gap="xs">
                <IconCheck size={16} color="green" />
                <Text size="sm">{ensembleResult.consensusFields} Consensus</Text>
              </Group>
              <Group gap="xs">
                <IconAlertCircle size={16} color="orange" />
                <Text size="sm">{ensembleResult.disagreementFields.length} Disagreements</Text>
              </Group>
            </Stack>
          </Group>

          {/* Overall Consensus Confidence Bar */}
          <Box mt="md">
            <Text size="xs" c="dimmed" mb={4}>
              Overall Ensemble Confidence
            </Text>
            <Progress
              value={ensembleResult.consensusConfidence * 100}
              size="lg"
              color={getConfidenceColor(ensembleResult.consensusConfidence)}
              label={`${(ensembleResult.consensusConfidence * 100).toFixed(1)}%`}
            />
          </Box>
        </Paper>

        {/* Field-by-Field Comparison Table */}
        <Box>
          <Group justify="space-between" mb="md">
            <Title order={5}>Field-by-Field Comparison</Title>
            <Badge size="sm" color="gray">
              {fieldComparisons.length} Fields
            </Badge>
          </Group>

          <ScrollArea h={400}>
            <Table striped highlightOnHover withTableBorder withColumnBorders>
              <thead>
                <tr>
                  <th>Field</th>
                  {ensembleResult.llmResults.map((llmResult) => (
                    <th key={llmResult.llmName}>{llmResult.llmName}</th>
                  ))}
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {fieldComparisons.map((comparison) => {
                  const isDisagreement =
                    ensembleResult.disagreementFields.includes(comparison.fieldId);

                  return (
                    <tr
                      key={comparison.fieldId}
                      style={{
                        backgroundColor: isDisagreement
                          ? 'var(--mantine-color-red-0)'
                          : undefined,
                      }}
                    >
                      <td>
                        <Text size="sm" fw={500}>
                          {comparison.fieldLabel}
                        </Text>
                      </td>
                      {ensembleResult.llmResults.map((llmResult) => {
                        const value = comparison.llmValues[llmResult.llmName];
                        const confidence = comparison.confidences[llmResult.llmName];

                        return (
                          <td key={llmResult.llmName}>
                            {value !== undefined ? (
                              <Group gap="xs">
                                <Text size="sm" lineClamp={1}>
                                  {typeof value === 'object'
                                    ? JSON.stringify(value)
                                    : String(value)}
                                </Text>
                                {confidence !== undefined && (
                                  <Badge size="xs" color={getConfidenceColor(confidence)}>
                                    {(confidence * 100).toFixed(0)}%
                                  </Badge>
                                )}
                              </Group>
                            ) : (
                              <Text size="sm" c="dimmed">
                                —
                              </Text>
                            )}
                          </td>
                        );
                      })}
                      <td>
                        {comparison.consensus ? (
                          <Badge color="green" leftSection={<IconCheck size={12} />}>
                            Consensus
                          </Badge>
                        ) : (
                          <Badge color="orange" leftSection={<IconAlertCircle size={12} />}>
                            Disagreement
                          </Badge>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </ScrollArea>
        </Box>

        {/* Disagreement Details */}
        {ensembleResult.disagreementFields.length > 0 && (
          <Paper withBorder p="md" bg="orange.0">
            <Group gap="xs" mb="sm">
              <IconAlertCircle size={20} color="orange" />
              <Text size="sm" fw={600}>
                Disagreement Details
              </Text>
            </Group>
            <Stack gap="xs">
              {ensembleResult.disagreementFields.slice(0, 5).map((fieldId) => {
                const comparison = fieldComparisons.find((c) => c.fieldId === fieldId);
                if (!comparison) return null;

                return (
                  <Box key={fieldId}>
                    <Text size="xs" fw={600}>
                      {comparison.fieldLabel}
                    </Text>
                    <Grid>
                      {ensembleResult.llmResults.map((llmResult) => {
                        const value = comparison.llmValues[llmResult.llmName];
                        return (
                          <Grid.Col key={llmResult.llmName} span={4}>
                            <Text size="xs" c="dimmed">
                              {llmResult.llmName}:
                            </Text>
                            <Text size="xs">
                              {value !== undefined
                                ? typeof value === 'object'
                                  ? JSON.stringify(value)
                                  : String(value)
                                : '—'}
                            </Text>
                          </Grid.Col>
                        );
                      })}
                    </Grid>
                  </Box>
                );
              })}
              {ensembleResult.disagreementFields.length > 5 && (
                <Text size="xs" c="dimmed" ta="center">
                  ... and {ensembleResult.disagreementFields.length - 5} more disagreements
                </Text>
              )}
            </Stack>
          </Paper>
        )}

        {/* Trust Summary */}
        <Paper withBorder p="md" bg="blue.0">
          <Group gap="xs" mb="sm">
            <IconShieldCheck size={20} color="blue" />
            <Text size="sm" fw={600}>
              AI Trust Summary
            </Text>
          </Group>
          <Text size="sm">
            {consensusPercentage >= 90
              ? `Excellent consensus across ${ensembleResult.llmResults.length} AI models. Template extraction is highly reliable.`
              : consensusPercentage >= 75
              ? `Good consensus across ${ensembleResult.llmResults.length} AI models. Minor disagreements require review.`
              : consensusPercentage >= 60
              ? `Moderate consensus. ${ensembleResult.disagreementFields.length} fields require human review before publication.`
              : `Low consensus. Template requires significant human review. ${ensembleResult.disagreementFields.length} fields show disagreements.`}
          </Text>
        </Paper>
      </Stack>
    </Card>
  );
};

export default MultiLLMComparisonDashboard;
